import React, { useState, useEffect, useContext } from 'react';
import { 
  ResponsiveContainer, 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend 
} from 'recharts';
import { ThemeContext } from '../contexts/ThemeContext';
import { getBaseUrl } from '../utils/apiUtils';
import { calculateFullTournamentRankings } from '../utils/tournamentScoring';
import './RankHistoryGraph.css';

const RankHistoryGraph = ({ tournamentId, historyList, username, friendsList, tournamentAppConfiguration }) => {
  const { theme } = useContext(ThemeContext);
  const [historyData, setHistoryData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [viewMode, setViewMode] = useState('overall');
  const [availableGames, setAvailableGames] = useState([]);

  const formatTimestampForDate = (timestamp) => {
    if (!timestamp || !timestamp.includes('T')) return timestamp;
    const parts = timestamp.split('T');
    const datePart = parts[0].replace(/:/g, '-');
    let timePart = parts[1];
    
    const colons = (timePart.match(/:/g) || []).length;
    if (colons >= 3) {
      const lastColonIndex = timePart.lastIndexOf(':');
      timePart = timePart.substring(0, lastColonIndex) + '.' + timePart.substring(lastColonIndex + 1);
    }
    return `${datePart}T${timePart}`;
  };

  useEffect(() => {
    if (!tournamentId || !historyList || historyList.length === 0) {
      setHistoryData([]);
      return;
    }

    const fetchAllHistory = async () => {
      setIsLoading(true);
      try {
        // Sample history if there are too many snapshots (limit to ~20 points for performance)
        const maxPoints = 20;
        let sampledHistory = [...historyList];
        if (historyList.length > maxPoints) {
          const step = Math.floor(historyList.length / maxPoints);
          sampledHistory = historyList.filter((_, index) => index % step === 0);
          // Always include the most recent
          if (sampledHistory[0].timestamp !== historyList[0].timestamp) {
            sampledHistory.unshift(historyList[0]);
          }
        }

        // Fetch each snapshot
        const fetchPromises = sampledHistory.map(async (h) => {
          const url = `${getBaseUrl()}${tournamentAppConfiguration.endPoints.results}${tournamentId}/${h.timestamp}`;
          const response = await fetch(url);
          if (!response.ok) return null;
          const data = await response.json();
          return {
            timestamp: h.timestamp,
            data
          };
        });

        const results = await Promise.all(fetchPromises);
        const validResults = results.filter(r => r !== null && r.data);

        const allGameTitles = new Set();
        
        // Process rankings for each snapshot
        const processedData = validResults.map(snapshot => {
          const rankings = calculateFullTournamentRankings(snapshot.data.games, tournamentAppConfiguration);
          const isoString = formatTimestampForDate(snapshot.timestamp);
          const dataPoint = {
            timestamp: snapshot.timestamp,
            displayTime: new Date(isoString).toLocaleString([], { 
              month: 'short', 
              day: 'numeric', 
              hour: '2-digit', 
              minute: '2-digit' 
            })
          };

          // Collect game titles
          if (snapshot.data.games) {
            snapshot.data.games.forEach(g => allGameTitles.add(g.title));
          }

          // Overall Rank
          const userRank = rankings.find(r => r.userName.toLowerCase() === username?.toLowerCase())?.rank;
          if (userRank) dataPoint.overall = userRank;

          // Friend Overall Ranks
          friendsList.forEach(friend => {
            const friendRank = rankings.find(r => r.userName.toLowerCase() === friend.toLowerCase())?.rank;
            if (friendRank) dataPoint[friend] = friendRank;
          });

          // Game Specific Ranks
          snapshot.data.games.forEach(game => {
            const userGameRank = game.leaderboard?.find(r => r.userName.toLowerCase() === username?.toLowerCase())?.rank;
            if (userGameRank) dataPoint[`game_${game.title}`] = userGameRank;

            friendsList.forEach(friend => {
              const friendGameRank = game.leaderboard?.find(r => r.userName.toLowerCase() === friend.toLowerCase())?.rank;
              if (friendGameRank) dataPoint[`game_${game.title}_${friend}`] = friendGameRank;
            });
          });

          return dataPoint;
        });

        setAvailableGames(Array.from(allGameTitles));
        // Sort by timestamp ascending for the graph
        setHistoryData(processedData.sort((a, b) => new Date(formatTimestampForDate(a.timestamp)) - new Date(formatTimestampForDate(b.timestamp))));
      } catch (err) {
        console.error('Error fetching history for graph:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllHistory();
  }, [tournamentId, historyList, username, friendsList, tournamentAppConfiguration]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className={`rank-history-tooltip ${theme}`}>
          <p className="tooltip-time">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="tooltip-rank" style={{ color: entry.color }}>
              {entry.name}: #{entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (isLoading) {
    return <div className="rank-history-loading">Analyzing historical trends...</div>;
  }

  if (historyData.length < 2) {
    return null; // Not enough data points to show a trend
  }

  return (
    <div className={`rank-history-card ${theme}`}>
      <div className="rank-history-header">
        <h3>Rank History</h3>
        <select 
          className={`view-mode-select ${theme}`}
          value={viewMode}
          onChange={(e) => setViewMode(e.target.value)}
        >
          <option value="overall">Overall Tournament Rank</option>
          {availableGames.map(game => (
            <option key={game} value={game}>{game} Rank</option>
          ))}
        </select>
      </div>

      <div className="rank-history-graph-wrapper">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={historyData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={theme === 'dark' ? '#444' : '#eee'} />
            <XAxis 
              dataKey="displayTime" 
              tick={{ fontSize: 10 }}
              stroke={theme === 'dark' ? '#888' : '#666'}
            />
            <YAxis 
              reversed 
              allowDecimals={false}
              tick={{ fontSize: 12 }}
              stroke={theme === 'dark' ? '#888' : '#666'}
              label={{ value: 'Rank', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: theme === 'dark' ? '#888' : '#666' } }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend verticalAlign="top" height={36}/>
            
            {viewMode === 'overall' ? (
              <>
                <Line 
                  type="monotone" 
                  dataKey="overall" 
                  name={username || "You"} 
                  stroke="#00f2ff" 
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#00f2ff' }}
                  activeDot={{ r: 6 }}
                />
                {friendsList.map((friend, index) => (
                  <Line 
                    key={friend}
                    type="monotone" 
                    dataKey={friend} 
                    name={friend} 
                    stroke={`hsl(${(index * 137) % 360}, 70%, 50%)`} 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </>
            ) : (
              <>
                <Line 
                  type="monotone" 
                  dataKey={`game_${viewMode}`} 
                  name={`${username || "You"}`} 
                  stroke="#00f2ff" 
                  strokeWidth={3}
                  dot={{ r: 4, fill: '#00f2ff' }}
                />
                {friendsList.map((friend, index) => (
                  <Line 
                    key={friend}
                    type="monotone" 
                    dataKey={`game_${viewMode}_${friend}`} 
                    name={friend} 
                    stroke={`hsl(${(index * 137) % 360}, 70%, 50%)`} 
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default RankHistoryGraph;
